import {
  Flex,
  Button,
  Grid,
  Text,
  ListItem,
  Box,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useState } from 'react';
import { withSiteExtensionData } from '../lib/hoc';
import { useTranslation } from '../lib/hooks.context';
import { createDate } from '../lib/utils.date';
import { Event } from '../types';
import EventTeaser from './EventTeaser';
import SiteSearch from './SiteSearch';
import OrderedList from './OrderedList';
import Tab from './Tab';

interface Props {
  events: Event[];
  isLoading?: boolean;
}

const getPastEvents = (events: Event[]) =>
  events
    .filter(
      ({ start_time, end_time }) =>
        createDate(end_time || start_time) < createDate(),
    )
    .sort(({ start_time: date }, { start_time: b }) =>
      createDate(date) > createDate(b) ? -1 : 0,
    );
const getUpcomingEvents = (events: Event[]) =>
  events
    .filter(
      ({ start_time, end_time }) =>
        createDate(end_time || start_time) > createDate(),
    )
    .sort(({ start_time: date }, { start_time: b }) =>
      createDate(date) < createDate(b) ? -1 : 0,
    );

const defaultLimit = 20;
const step = defaultLimit;

// State always holds people and filtered is either a subset of that or false
interface State {
  limit: number;
  current: boolean;
}

const TabContent = ({
  events,
  limit,
  current,
  onShowMore,
}: Pick<Props, 'events'> &
  Pick<State, 'limit'> & { onShowMore: () => void; current?: boolean }) => {
  const t = useTranslation();

  return (
    <>
      <Grid
        templateColumns="repeat(auto-fit, minmax(275px, 1fr))"
        gridColumnGap={['6', '10']}
        gridRowGap={['6', '10', null, '16']}
        mb="10"
        as={OrderedList}
      >
        {events?.slice(0, limit).map((event) => (
          <EventTeaser
            key={`${event.id}-${event.site}`}
            {...event}
            as={ListItem}
          />
        ))}
        {!events?.length ? (
          <Text>{t(current ? 'events_no_upcoming' : 'events_no_past')}</Text>
        ) : null}
      </Grid>
      {events?.length > limit ? (
        <Flex as="footer" justifyContent="center">
          <Button onClick={onShowMore} variant="solidGreen" size="large">
            {t('load_more')}
          </Button>
        </Flex>
      ) : null}
    </>
  );
};

const EventList = ({ events: allEvents, isLoading }: Props) => {
  const [{ limit }, setShownEvents] = useState<State>({
    limit: defaultLimit,
    current: true,
  });
  const t = useTranslation();

  const showMore = () => {
    setShownEvents((s) => ({
      ...s,
      limit: s.limit + step,
    }));
  };

  return (
    <Box as="section">
      <Tabs size="lg">
        <Flex
          as="header"
          mb="10"
          gridGap="10"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          sx={{ '> *': { width: 'auto' } }}
        >
          <TabList borderBottom="none" mb={5}>
            <Tab>{t('events_incoming')}</Tab>
            <Tab>{t('events_past')}</Tab>
          </TabList>
          <SiteSearch isLoading={isLoading} justifyContent="flex-end" />
        </Flex>
        {isLoading ? null : (
          <TabPanels>
            <TabPanel px={0}>
              <TabContent
                events={getUpcomingEvents(allEvents)}
                onShowMore={showMore}
                limit={limit}
                current
              />
            </TabPanel>
            <TabPanel px={0}>
              <TabContent
                events={getPastEvents(allEvents)}
                onShowMore={showMore}
                limit={limit}
              />
            </TabPanel>
          </TabPanels>
        )}
      </Tabs>
    </Box>
  );
};

export default withSiteExtensionData<Pick<Props, 'events'>>(EventList, {
  param: 'sites',
  dataKey: 'events',
});
